import { RecordValues } from '@wyz/types';
import {
  Button,
  CenteredSpinner,
  FormatDate,
  Table,
  Text,
  Title,
} from '@wyz/ui';
import { DateUtils } from '@wyz/utils';
import { isEmpty, pick } from 'lodash-es';
import { useManageParameters, useManageVehicleTypeGroups } from '~/common/api';
import { Trans, useI18n, useLocale } from '~/common/context';
import { IMinimalBrand } from '~/common/types';
import { useManageSearchHistory } from '~/purchase/api';
import { ListTooltip } from '~/purchase/components';
import {
  PurchaseSearchHelpers,
  SearchHistoryHelpers,
} from '~/purchase/helpers';
import { IQuoteListState } from '~/purchase/types';

type Props = {
  onSearch: (payload: Partial<IQuoteListState>) => void;
  brands: Array<IMinimalBrand>;
  type: RecordValues<typeof SearchHistoryHelpers.SEARCH_HISTORY_TYPE>;
};
function SearchHistoryList({ onSearch, brands, type }: Props) {
  const locale = useLocale();
  const manageSearchHistory = useManageSearchHistory({
    list: {
      enabled: true,
      args: { type: type },
    },
  });

  const manageParameters = useManageParameters({
    getSeasons: {
      enabled: true,
    },
  });
  const manageVehicleTypeGroups = useManageVehicleTypeGroups({
    coreVehicleTypeGroup: { enabled: true },
  });
  const { t } = useI18n();
  return (
    <>
      <Title heading='lg' color='secondary-gray' weight='bold'>
        <Trans i18nKey='app.purchase.search_page.history.title' />
      </Title>
      {(manageSearchHistory.list.isProcessing ||
        manageParameters.getSeasons.isProcessing ||
        manageVehicleTypeGroups.coreVehicleTypeGroup.isProcessing) && (
        <CenteredSpinner />
      )}
      <Table>
        <thead>
          <tr>
            <td></td>
            <td align='center'>
              <Trans i18nKey='app.purchase.search_page.vehicle_type' />
            </td>
            <td align='center'>
              <Trans i18nKey='app.purchase.search_page.selected_dimension' />
            </td>
            <td align='center'>
              <Trans i18nKey='app.common.product.season' />
            </td>
            <td align='center'>
              <Trans i18nKey='app.common.product.brands' />
            </td>
            <td align='center'>
              <Trans i18nKey='app.common.search.include_higher_indexes' />
            </td>
            <td align='center'>
              <Trans i18nKey='app.common.quantity' />
            </td>
            <td></td>
          </tr>
        </thead>
        <tbody>
          {manageParameters.getSeasons.isSuccess &&
            manageVehicleTypeGroups.coreVehicleTypeGroup.isSuccess &&
            manageSearchHistory.list.isSuccess &&
            manageSearchHistory.list.data?.data.map((searchEntry) => {
              const vehicleGroup =
                manageVehicleTypeGroups.coreVehicleTypeGroup.data?.data.find(
                  (group) => group.name === searchEntry.query.vehicleGroupCode,
                );
              return (
                <tr key={searchEntry.id}>
                  <td
                    className='align-middle'
                    align='center'
                    style={{ width: '12%' }}
                  >
                    <Text color='secondary-gray' heading='sm'>
                      <FormatDate
                        date={searchEntry.searchedAt}
                        format={DateUtils.DATE_FORMAT.RELATIVE_TIME}
                        locale={locale.locale}
                      />
                    </Text>
                  </td>
                  <td
                    className='align-middle'
                    align='center'
                    style={{ width: '13%' }}
                  >
                    {vehicleGroup ? (
                      <Text color='secondary' heading='sm' weight='semi-bold'>
                        <Trans i18nKey={vehicleGroup?.i18nKey}>
                          {vehicleGroup?.name}
                        </Trans>
                      </Text>
                    ) : (
                      searchEntry.query.vehicleGroupCode
                    )}
                  </td>
                  <td
                    className='align-middle'
                    align='center'
                    style={{ width: '13%' }}
                  >
                    <Text color='secondary' heading='sm' weight='semi-bold'>
                      {PurchaseSearchHelpers.formatDimension({
                        width: searchEntry.query.width,
                        height: searchEntry.query.height,
                        diameter: searchEntry.query.diameter,
                        loadIndex: searchEntry.query.loadIndexes?.[0],
                        speedIndex: searchEntry.query.speedIndexes?.[0],
                        identifier: searchEntry.query.identifiers?.[0],
                      })}
                    </Text>
                  </td>
                  <td
                    className='align-middle'
                    align='center'
                    style={{ width: '13%' }}
                  >
                    <Text color='secondary' heading='sm' weight='semi-bold'>
                      {isEmpty(searchEntry.query.seasons)
                        ? t('app.common.all_female')
                        : searchEntry.query.seasons
                            .map((seasonId) => {
                              const seasonItem =
                                manageParameters.getSeasons.data?.data.find(
                                  (season) => season.publicKey === seasonId,
                                );
                              return seasonItem?.i18nKey
                                ? t(seasonItem.i18nKey)
                                : seasonItem?.name;
                            })
                            ?.join(',')}
                    </Text>
                  </td>
                  <td
                    className='align-middle'
                    align='center'
                    style={{ width: '13%' }}
                  >
                    <Text color='secondary' heading='sm' weight='semi-bold'>
                      {isEmpty(searchEntry.query.brands) ? (
                        t('app.common.all_female')
                      ) : (
                        <ListTooltip
                          list={searchEntry.query.brands.map(
                            (brandPublicKey) =>
                              brands.find(
                                (brand) => brand.publicKey === brandPublicKey,
                              )?.name as string,
                          )}
                        />
                      )}
                    </Text>
                  </td>
                  <td
                    className='align-middle'
                    align='center'
                    style={{ width: '6%' }}
                  >
                    <Text color='secondary-gray' heading='xs' weight='normal'>
                      {searchEntry.query.includeHigherIndexes
                        ? t('app.common.messages.yes')
                        : t('app.common.messages.no')}
                    </Text>
                  </td>
                  <td
                    className='align-middle'
                    align='center'
                    style={{ width: '10%' }}
                  >
                    <Text color='secondary' heading='sm' weight='semi-bold'>
                      <Trans
                        i18nKey='app.purchase.search_page.history.quantity_value'
                        values={{
                          value: searchEntry.query.quantity,
                        }}
                      />
                    </Text>
                  </td>
                  <td
                    className='align-middle'
                    align='center'
                    style={{ width: '20%' }}
                  >
                    <Button
                      id='relaunch'
                      variant='tertiary-blue'
                      onClick={() => {
                        onSearch({
                          searchQuery: {
                            vehicleTypeGroup:
                              searchEntry.query.vehicleGroupCode,
                            identifier:
                              searchEntry.query.identifiers &&
                              searchEntry.query.identifiers.length > 0
                                ? {
                                    value: searchEntry.query.identifiers[0],
                                    label: searchEntry.query.identifiers[0],
                                  }
                                : null,
                            // for BC compatibility, empty seasons means all seasons
                            seasons: isEmpty(searchEntry.query.seasons)
                              ? (manageParameters.getSeasons.data?.data.map(
                                  (season) => season.publicKey,
                                ) ?? [])
                              : searchEntry.query.seasons,
                            ...pick(searchEntry.query, [
                              'quantity',
                              'brands',
                              'width',
                              'height',
                              'diameter',
                              'includeHigherIndexes',
                              'loadIndexes',
                              'speedIndexes',
                              'fieldNameSearch',
                            ]),

                            vehicleTypes: searchEntry.query.vehicleTypeIds,
                          },
                        });
                      }}
                    >
                      <Trans i18nKey='app.purchase.search_page.history.relaunch_button' />
                    </Button>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </Table>
    </>
  );
}

export default SearchHistoryList;
